import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useRef, useState } from "react";
import { editSummon } from "services/advocateComplaintService";
import { AdvocateComplaints, Investigation, Summon } from "types";
import { LoadingIcon } from "utils/constant";
import { ADVOCATE_SUMMON_STATUS } from "utils/constantValues";
import { useTranslation } from "contexts/i18nContext";
import "../../complaintRequest/styles/AddVictimModal.css";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: Summon;
  complaintData: AdvocateComplaints;
  investigationData: Investigation;
  onSummonRefresh: any;
}

export default function AddLetterModal({
  isOpen,
  setIsOpen,
  summonData,
  complaintData,
  investigationData,
  onSummonRefresh,
}: ModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const summonFileInputRef = useRef<HTMLInputElement>(null);

  const [formState, setFormState] = useState({
    hasSummon: true,
    hearing_date: "",
    region: "",
    zone: "",
    woreda: "",
    kebele: "",
    summary: "",
    files: [] as File[],
    summonFiles: [] as File[],
    investigation: null,
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSummonFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        summonFiles: [...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!investigationData?.uu_id) {
      showToast(t("investigationNotFound"), "error");
      return;
    }

    if (formState?.files?.length <= 0) {
      showToast(t("letterFileRequired"), "error");
      return;
    }

    setLoading(true);
    const data = new FormData();
    data.append("letter_upload_files", formState?.files[0] || "");
    data.append("status", ADVOCATE_SUMMON_STATUS.FORWAREDED_TO_ACCUSED);
    data.append("investigation", investigationData?.uu_id);
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append("accused_advocate_name", complaintData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      complaintData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      complaintData?.accused_license_number || "",
    );

    if (formState.hasSummon) {
      data.append("hearing_date", formState?.hearing_date);
      data.append("region", formState?.region);
      data.append("zone", formState?.zone);
      data.append("woreda", formState?.woreda);
      data.append("kebele", formState?.kebele);
      data.append("summary", formState?.summary);
      data.append("summon_files", formState?.summonFiles[0] || "");
      data.append("investigation", investigationData?.uu_id);
      data.append("status", ADVOCATE_SUMMON_STATUS.FORWARDED_TO_REGISTRAR_FOR);
    }

    const isFormData = true;
    const summon_id = summonData?.id || "";

    try {
      // await addSummon(data, isFormData);
      await editSummon(data, summon_id, isFormData);

      onSummonRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        hasSummon: true,
        hearing_date: "",
        region: "",
        zone: "",
        woreda: "",
        kebele: "",
        summary: "",
        files: [] as File[],
        summonFiles: [] as File[],
        investigation: null,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const triggerSummonFileInput = () => {
    summonFileInputRef.current?.click();
  };
  if (!isOpen) return null;

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={t("createLetter")}>
      <form onSubmit={handleSubmit} className="victim-form">
        {/* <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("letterDocument")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState?.files && formState?.files?.length > 0 && (
              <div className="file-list">
                {formState?.files?.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section> */}

        <div className="mb-4">
          <div className="flex items-center gap-2 bg-gray-100 border p-2 rounded-md">
            <input
              className="form-checkbox h-4 w-4 text-blue-600"
              type="checkbox"
              name="hasSummon"
              checked={formState.hasSummon}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  hasSummon: !prev.hasSummon,
                }))
              }
            />
            <span className="text-sm text-gray-700">{t("sendSummonToo")}</span>
          </div>
        </div>

        {formState.hasSummon && (
          <>
            <section className="victim-section">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("hearingDate")}
                </label>
                <input
                  required
                  className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                  type={"date"}
                  name="hearing_date"
                  value={formState.hearing_date}
                  onChange={handleChange}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("region")}
                  </label>
                  <input
                    required
                    className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                    type="text"
                    name="region"
                    value={formState.region}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("zone")}
                  </label>
                  <input
                    required
                    className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                    type="text"
                    name="zone"
                    value={formState.zone}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("woreda")}
                  </label>
                  <input
                    required
                    className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                    type="text"
                    name="woreda"
                    value={formState.woreda}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("kebele")}
                  </label>
                  <input
                    required
                    className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                    type="text"
                    name="kebele"
                    value={formState.kebele}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("summary")}
                </label>
                <textarea
                  required
                  rows={3}
                  name="summary"
                  value={formState.summary}
                  onChange={handleChange}
                  placeholder={t("detailsHere")}
                  className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                />
              </div>
            </section>

            <section className="evidence-section">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("summonFile")}
              </label>
              <div
                className="file-upload-area"
                onClick={triggerSummonFileInput}
              >
                <input
                  type="file"
                  ref={summonFileInputRef}
                  onChange={handleSummonFileChange}
                  style={{ display: "none" }}
                />
                <p className="upload-text">{t("uploadFiles")}</p>
                {formState?.summonFiles &&
                  formState?.summonFiles?.length > 0 && (
                    <div className="file-list">
                      {formState?.summonFiles?.map((file, index) => (
                        <div key={index} className="file-item">
                          {file.name}
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </section>
          </>
        )}

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon} {t("submitting")}
            </div>
          ) : (
            t("createSummon")
          )}
        </button>
      </form>
    </SideModal>
  );
}
